import React, { useCallback, useMemo } from 'react';
import { Add as AddIcon, AutoAwesome, InfoOutlined } from '@mui/icons-material';
import { Autocomplete, Box, Button, Chip, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

export type ICD10Category =
  | 'Common'
  | 'Depression'
  | 'Anxiety'
  | 'Trauma & Stress'
  | 'Bipolar'
  | 'Psychotic Disorders'
  | 'ADHD & Neurodevelopmental'
  | 'Sleep'
  | 'OCD & Related'
  | 'Eating Disorders'
  | 'Personality Disorders'
  | 'Substance Use - Alcohol'
  | 'Substance Use - Opioids'
  | 'Substance Use - Cannabis'
  | 'Substance Use - Stimulants'
  | 'Substance Use - Sedatives'
  | 'Substance Use - Nicotine'
  | 'Substance Use - Other'
  | 'Dissociative'
  | 'Somatic'
  | 'Cognitive'
  | 'Behavioral & Other Mental'
  | 'Z-Codes (Psychosocial/Context)'
  | 'Z-Codes (Medication/Adherence)'
  | 'Z-Codes (Family/Relationship)'
  | 'Z-Codes (Work/School)'
  | 'Z-Codes (Housing/Econ)'
  | 'Z-Codes (Legal/Forensic)'
  | 'Z-Codes (Other)';

export type ICD10CodeOption = {
  icd10: string;
  description: string;
  category: ICD10Category;
  /** Optional extra search strings for autocomplete */
  synonyms?: string[];
  /** When true, this is more of a "status/modifier/context" than a core psychiatric dx */
  isModifierLike?: boolean;
};

export const ICD10_CODES: ICD10CodeOption[] = [
  // -----------------------
  // Most common (put up top)
  // -----------------------
  {
    icd10: 'F41.1',
    description: 'Generalized anxiety disorder',
    category: 'Common',
    synonyms: ['GAD', 'anxiety'],
  },
  {
    icd10: 'F33.1',
    description: 'Major depressive disorder, recurrent, moderate',
    category: 'Common',
    synonyms: ['MDD recurrent moderate', 'depression'],
  },
  {
    icd10: 'F33.0',
    description: 'Major depressive disorder, recurrent, mild',
    category: 'Common',
    synonyms: ['MDD recurrent mild', 'depression'],
  },
  {
    icd10: 'F33.2',
    description: 'Major depressive disorder, recurrent, severe w/o psychotic features',
    category: 'Common',
    synonyms: ['MDD recurrent severe'],
  },
  {
    icd10: 'F32.1',
    description: 'Major depressive disorder, single episode, moderate',
    category: 'Common',
    synonyms: ['MDD single moderate'],
  },
  {
    icd10: 'F32.0',
    description: 'Major depressive disorder, single episode, mild',
    category: 'Common',
    synonyms: ['MDD single mild'],
  },
  {
    icd10: 'F43.10',
    description: 'Post-traumatic stress disorder, unspecified',
    category: 'Common',
    synonyms: ['PTSD'],
  },
  {
    icd10: 'F90.2',
    description: 'Attention-deficit hyperactivity disorder, combined type',
    category: 'Common',
    synonyms: ['ADHD combined'],
  },
  {
    icd10: 'F90.0',
    description: 'Attention-deficit hyperactivity disorder, predominantly inattentive type',
    category: 'Common',
    synonyms: ['ADHD inattentive', 'ADD'],
  },
  {
    icd10: 'F50.81',
    description: 'Binge eating disorder',
    category: 'Common',
    synonyms: ['BED', 'binge eating'],
  },
  { icd10: 'F51.01', description: 'Primary insomnia', category: 'Common', synonyms: ['insomnia'] },
  {
    icd10: 'F51.05',
    description: 'Insomnia due to other mental disorder',
    category: 'Common',
    synonyms: ['insomnia', 'sleep'],
  },
  {
    icd10: 'F42.2',
    description: 'Mixed obsessional thoughts and acts',
    category: 'Common',
    synonyms: ['OCD'],
  },
  {
    icd10: 'F60.3',
    description: 'Borderline personality disorder',
    category: 'Common',
    synonyms: ['BPD'],
  },
  {
    icd10: 'F10.20',
    description: 'Alcohol dependence, uncomplicated',
    category: 'Common',
    synonyms: ['AUD', 'alcohol use disorder'],
  },
  {
    icd10: 'F11.20',
    description: 'Opioid dependence, uncomplicated',
    category: 'Common',
    synonyms: ['OUD', 'opioid use disorder'],
  },
  {
    icd10: 'F17.200',
    description: 'Nicotine dependence, unspecified, uncomplicated',
    category: 'Common',
    synonyms: ['tobacco use', 'nicotine'],
  },
  {
    icd10: 'F12.20',
    description: 'Cannabis dependence, uncomplicated',
    category: 'Common',
    synonyms: ['cannabis use disorder'],
  },

  // -------------
  // Depression
  // -------------
  {
    icd10: 'F32.9',
    description: 'Major depressive disorder, single episode, unspecified',
    category: 'Depression',
  },
  {
    icd10: 'F32.2',
    description: 'Major depressive disorder, single episode, severe w/o psychotic features',
    category: 'Depression',
  },
  {
    icd10: 'F32.3',
    description: 'Major depressive disorder, single episode, severe with psychotic features',
    category: 'Depression',
  },
  {
    icd10: 'F33.9',
    description: 'Major depressive disorder, recurrent, unspecified',
    category: 'Depression',
  },
  {
    icd10: 'F33.3',
    description: 'Major depressive disorder, recurrent, severe with psychotic features',
    category: 'Depression',
  },
  {
    icd10: 'F33.41',
    description: 'Major depressive disorder, recurrent, in partial remission',
    category: 'Depression',
    isModifierLike: true,
    synonyms: ['partial remission'],
  },
  {
    icd10: 'F33.42',
    description: 'Major depressive disorder, recurrent, in full remission',
    category: 'Depression',
    isModifierLike: true,
    synonyms: ['full remission'],
  },
  {
    icd10: 'F34.1',
    description: 'Dysthymia (persistent depressive disorder)',
    category: 'Depression',
    synonyms: ['PDD', 'dysthymic'],
  },
  {
    icd10: 'F34.0',
    description: 'Cyclothymic disorder',
    category: 'Depression',
    synonyms: ['cyclothymia'],
  },
  {
    icd10: 'F53.0',
    description: 'Postpartum depression',
    category: 'Depression',
    synonyms: ['postpartum', 'peripartum'],
  },

  // ----------
  // Anxiety
  // ----------
  {
    icd10: 'F41.0',
    description: 'Panic disorder [episodic paroxysmal anxiety]',
    category: 'Anxiety',
    synonyms: ['panic attacks'],
  },
  { icd10: 'F41.9', description: 'Anxiety disorder, unspecified', category: 'Anxiety' },
  {
    icd10: 'F40.10',
    description: 'Social phobia, unspecified',
    category: 'Anxiety',
    synonyms: ['social anxiety'],
  },
  { icd10: 'F40.00', description: 'Agoraphobia, unspecified', category: 'Anxiety' },
  { icd10: 'F40.01', description: 'Agoraphobia with panic disorder', category: 'Anxiety' },
  { icd10: 'F40.11', description: 'Social phobia, generalized', category: 'Anxiety' },
  {
    icd10: 'F40.231',
    description: 'Fear of flying',
    category: 'Anxiety',
    synonyms: ['phobia', 'aviophobia'],
  },
  { icd10: 'F40.240', description: 'Claustrophobia', category: 'Anxiety', synonyms: ['phobia'] },
  {
    icd10: 'F40.218',
    description: 'Other specified phobia',
    category: 'Anxiety',
    synonyms: ['phobia'],
  },

  // ----------------
  // Trauma & Stress
  // ----------------
  {
    icd10: 'F43.11',
    description: 'Post-traumatic stress disorder, acute',
    category: 'Trauma & Stress',
    isModifierLike: true,
    synonyms: ['PTSD acute'],
  },
  {
    icd10: 'F43.12',
    description: 'Post-traumatic stress disorder, chronic',
    category: 'Trauma & Stress',
    isModifierLike: true,
    synonyms: ['PTSD chronic'],
  },
  {
    icd10: 'F43.0',
    description: 'Acute stress reaction',
    category: 'Trauma & Stress',
    synonyms: ['acute stress'],
  },
  {
    icd10: 'F43.21',
    description: 'Adjustment disorder with depressed mood',
    category: 'Trauma & Stress',
    synonyms: ['adjustment disorder'],
  },
  {
    icd10: 'F43.22',
    description: 'Adjustment disorder with anxiety',
    category: 'Trauma & Stress',
    synonyms: ['adjustment disorder'],
  },
  {
    icd10: 'F43.23',
    description: 'Adjustment disorder with mixed anxiety and depressed mood',
    category: 'Trauma & Stress',
    synonyms: ['adjustment disorder'],
  },
  {
    icd10: 'F43.24',
    description: 'Adjustment disorder with disturbance of conduct',
    category: 'Trauma & Stress',
  },
  {
    icd10: 'F43.25',
    description: 'Adjustment disorder with mixed disturbance of emotions and conduct',
    category: 'Trauma & Stress',
  },
  { icd10: 'F43.20', description: 'Adjustment disorder, unspecified', category: 'Trauma & Stress' },
  { icd10: 'F43.8', description: 'Other reactions to severe stress', category: 'Trauma & Stress' },
  {
    icd10: 'F43.9',
    description: 'Reaction to severe stress, unspecified',
    category: 'Trauma & Stress',
  },

  // --------
  // Bipolar
  // --------
  { icd10: 'F31.9', description: 'Bipolar disorder, unspecified', category: 'Bipolar' },
  {
    icd10: 'F31.0',
    description: 'Bipolar disorder, current episode hypomanic',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.1',
    description: 'Bipolar disorder, current episode manic w/o psychotic features',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.2',
    description: 'Bipolar disorder, current episode manic with psychotic features',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.30',
    description: 'Bipolar disorder, current episode depressed, mild/moderate, unspecified',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.31',
    description: 'Bipolar disorder, current episode depressed, mild',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.32',
    description: 'Bipolar disorder, current episode depressed, moderate',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.4',
    description: 'Bipolar disorder, current episode depressed, severe w/o psychotic features',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.5',
    description: 'Bipolar disorder, current episode depressed, severe with psychotic features',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.70',
    description: 'Bipolar disorder, currently in remission, most recent episode unspecified',
    category: 'Bipolar',
    isModifierLike: true,
    synonyms: ['remission'],
  },
  {
    icd10: 'F31.81',
    description: 'Bipolar II disorder',
    category: 'Bipolar',
    synonyms: ['bipolar 2'],
  },

  // ------------------
  // Psychotic disorders
  // ------------------
  { icd10: 'F20.9', description: 'Schizophrenia, unspecified', category: 'Psychotic Disorders' },
  { icd10: 'F20.0', description: 'Paranoid schizophrenia', category: 'Psychotic Disorders' },
  {
    icd10: 'F25.0',
    description: 'Schizoaffective disorder, bipolar type',
    category: 'Psychotic Disorders',
  },
  {
    icd10: 'F25.1',
    description: 'Schizoaffective disorder, depressive type',
    category: 'Psychotic Disorders',
  },
  {
    icd10: 'F29',
    description: 'Unspecified psychosis not due to a substance or known physiological condition',
    category: 'Psychotic Disorders',
    synonyms: ['psychosis NOS'],
  },
  { icd10: 'F22', description: 'Delusional disorders', category: 'Psychotic Disorders' },

  // ---------------------------
  // ADHD & Neurodevelopmental
  // ---------------------------
  {
    icd10: 'F90.1',
    description: 'ADHD, predominantly hyperactive type',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['ADHD hyperactive'],
  },
  {
    icd10: 'F98.8',
    description:
      'Other specified behavioral and emotional disorders with onset usually in childhood',
    category: 'ADHD & Neurodevelopmental',
  },
  {
    icd10: 'F84.0',
    description: 'Autistic disorder (Autism spectrum disorder)',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['ASD', 'autism'],
  },
  {
    icd10: 'F84.5',
    description: "Asperger's syndrome",
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['aspergers'],
  },
  {
    icd10: 'F81.9',
    description: 'Developmental disorder of scholastic skills, unspecified',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['learning disorder'],
  },

  // ------
  // Sleep
  // ------
  { icd10: 'G47.00', description: 'Insomnia, unspecified', category: 'Sleep', synonyms: ['sleep'] },
  {
    icd10: 'G47.10',
    description: 'Hypersomnia, unspecified',
    category: 'Sleep',
    synonyms: ['sleepy'],
  },
  {
    icd10: 'G47.33',
    description: 'Obstructive sleep apnea (adult)',
    category: 'Sleep',
    synonyms: ['OSA', 'sleep apnea'],
  },
  {
    icd10: 'F51.09',
    description: 'Other insomnia not due to a substance or known physiological condition',
    category: 'Sleep',
    synonyms: ['insomnia'],
  },

  // --------------
  // OCD & Related
  // --------------
  {
    icd10: 'F42.9',
    description: 'Obsessive-compulsive disorder, unspecified',
    category: 'OCD & Related',
    synonyms: ['OCD'],
  },
  {
    icd10: 'F42.3',
    description: 'Hoarding disorder',
    category: 'OCD & Related',
    synonyms: ['hoarding'],
  },
  {
    icd10: 'F42.4',
    description: 'Excoriation (skin-picking) disorder',
    category: 'OCD & Related',
    synonyms: ['skin picking'],
  },
  {
    icd10: 'F45.22',
    description: 'Body dysmorphic disorder',
    category: 'OCD & Related',
    synonyms: ['BDD'],
  },
  {
    icd10: 'F63.3',
    description: 'Trichotillomania',
    category: 'OCD & Related',
    synonyms: ['hair pulling'],
  },

  // ----------------
  // Eating Disorders
  // ----------------
  { icd10: 'F50.00', description: 'Anorexia nervosa, unspecified', category: 'Eating Disorders' },
  {
    icd10: 'F50.01',
    description: 'Anorexia nervosa, restricting type',
    category: 'Eating Disorders',
  },
  {
    icd10: 'F50.02',
    description: 'Anorexia nervosa, binge eating/purging type',
    category: 'Eating Disorders',
  },
  { icd10: 'F50.2', description: 'Bulimia nervosa', category: 'Eating Disorders' },
  { icd10: 'F50.9', description: 'Eating disorder, unspecified', category: 'Eating Disorders' },

  // ----------------------
  // Personality Disorders
  // ----------------------
  {
    icd10: 'F60.0',
    description: 'Paranoid personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.1',
    description: 'Schizoid personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.2',
    description: 'Antisocial personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.4',
    description: 'Histrionic personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.5',
    description: 'Obsessive-compulsive personality disorder',
    category: 'Personality Disorders',
    synonyms: ['OCPD'],
  },
  {
    icd10: 'F60.6',
    description: 'Avoidant personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.7',
    description: 'Dependent personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.81',
    description: 'Narcissistic personality disorder',
    category: 'Personality Disorders',
  },

  // -------------------------
  // Substance Use — Alcohol
  // -------------------------
  {
    icd10: 'F10.10',
    description: 'Alcohol abuse, uncomplicated',
    category: 'Substance Use - Alcohol',
    synonyms: ['AUD mild'],
  },
  {
    icd10: 'F10.20',
    description: 'Alcohol dependence, uncomplicated',
    category: 'Substance Use - Alcohol',
    synonyms: ['AUD'],
  },
  {
    icd10: 'F10.21',
    description: 'Alcohol dependence, in remission',
    category: 'Substance Use - Alcohol',
    isModifierLike: true,
    synonyms: ['AUD remission'],
  },
  {
    icd10: 'F10.23',
    description: 'Alcohol dependence with withdrawal',
    category: 'Substance Use - Alcohol',
    isModifierLike: true,
    synonyms: ['withdrawal'],
  },
  {
    icd10: 'F10.24',
    description: 'Alcohol dependence with alcohol-induced mood disorder',
    category: 'Substance Use - Alcohol',
    isModifierLike: true,
  },

  // -------------------------
  // Substance Use — Opioids
  // -------------------------
  {
    icd10: 'F11.10',
    description: 'Opioid abuse, uncomplicated',
    category: 'Substance Use - Opioids',
    synonyms: ['OUD mild'],
  },
  {
    icd10: 'F11.20',
    description: 'Opioid dependence, uncomplicated',
    category: 'Substance Use - Opioids',
    synonyms: ['OUD'],
  },
  {
    icd10: 'F11.21',
    description: 'Opioid dependence, in remission',
    category: 'Substance Use - Opioids',
    isModifierLike: true,
    synonyms: ['OUD remission'],
  },
  {
    icd10: 'F11.22',
    description: 'Opioid dependence with intoxication',
    category: 'Substance Use - Opioids',
    isModifierLike: true,
  },
  {
    icd10: 'F11.23',
    description: 'Opioid dependence with withdrawal',
    category: 'Substance Use - Opioids',
    isModifierLike: true,
    synonyms: ['withdrawal'],
  },
  {
    icd10: 'F11.24',
    description: 'Opioid dependence with opioid-induced mood disorder',
    category: 'Substance Use - Opioids',
    isModifierLike: true,
  },
  {
    icd10: 'F11.29',
    description: 'Opioid dependence with unspecified opioid-induced disorder',
    category: 'Substance Use - Opioids',
    isModifierLike: true,
  },

  // -------------------------
  // Substance Use — Cannabis
  // -------------------------
  {
    icd10: 'F12.10',
    description: 'Cannabis abuse, uncomplicated',
    category: 'Substance Use - Cannabis',
  },
  {
    icd10: 'F12.20',
    description: 'Cannabis dependence, uncomplicated',
    category: 'Substance Use - Cannabis',
  },
  {
    icd10: 'F12.21',
    description: 'Cannabis dependence, in remission',
    category: 'Substance Use - Cannabis',
    isModifierLike: true,
  },
  {
    icd10: 'F12.22',
    description: 'Cannabis dependence with intoxication',
    category: 'Substance Use - Cannabis',
    isModifierLike: true,
  },
  {
    icd10: 'F12.23',
    description: 'Cannabis dependence with withdrawal',
    category: 'Substance Use - Cannabis',
    isModifierLike: true,
  },

  // --------------------------
  // Substance Use — Stimulants
  // --------------------------
  {
    icd10: 'F15.10',
    description: 'Other stimulant abuse, uncomplicated',
    category: 'Substance Use - Stimulants',
    synonyms: ['amphetamine', 'meth'],
  },
  {
    icd10: 'F15.20',
    description: 'Other stimulant dependence, uncomplicated',
    category: 'Substance Use - Stimulants',
    synonyms: ['amphetamine', 'meth'],
  },
  {
    icd10: 'F15.21',
    description: 'Other stimulant dependence, in remission',
    category: 'Substance Use - Stimulants',
    isModifierLike: true,
  },
  {
    icd10: 'F15.23',
    description: 'Other stimulant dependence with withdrawal',
    category: 'Substance Use - Stimulants',
    isModifierLike: true,
  },
  {
    icd10: 'F14.10',
    description: 'Cocaine abuse, uncomplicated',
    category: 'Substance Use - Stimulants',
    synonyms: ['cocaine'],
  },
  {
    icd10: 'F14.20',
    description: 'Cocaine dependence, uncomplicated',
    category: 'Substance Use - Stimulants',
    synonyms: ['cocaine'],
  },
  {
    icd10: 'F14.21',
    description: 'Cocaine dependence, in remission',
    category: 'Substance Use - Stimulants',
    isModifierLike: true,
  },

  // --------------------------
  // Substance Use — Sedatives
  // --------------------------
  {
    icd10: 'F13.10',
    description: 'Sedative, hypnotic, or anxiolytic abuse, uncomplicated',
    category: 'Substance Use - Sedatives',
    synonyms: ['benzo'],
  },
  {
    icd10: 'F13.20',
    description: 'Sedative, hypnotic, or anxiolytic dependence, uncomplicated',
    category: 'Substance Use - Sedatives',
    synonyms: ['benzo'],
  },
  {
    icd10: 'F13.21',
    description: 'Sedative, hypnotic, or anxiolytic dependence, in remission',
    category: 'Substance Use - Sedatives',
    isModifierLike: true,
  },
  {
    icd10: 'F13.23',
    description: 'Sedative, hypnotic, or anxiolytic dependence with withdrawal',
    category: 'Substance Use - Sedatives',
    isModifierLike: true,
  },

  // -------------------------
  // Substance Use — Nicotine
  // -------------------------
  {
    icd10: 'F17.200',
    description: 'Nicotine dependence, unspecified, uncomplicated',
    category: 'Substance Use - Nicotine',
    synonyms: ['tobacco', 'nicotine'],
  },
  {
    icd10: 'F17.201',
    description: 'Nicotine dependence, unspecified, in remission',
    category: 'Substance Use - Nicotine',
    isModifierLike: true,
    synonyms: ['remission'],
  },
  {
    icd10: 'Z72.0',
    description: 'Tobacco use, not otherwise specified',
    category: 'Substance Use - Nicotine',
    isModifierLike: true,
    synonyms: ['tobacco use'],
  },

  // -------------------------
  // Substance Use — Other
  // -------------------------
  {
    icd10: 'F19.10',
    description: 'Other psychoactive substance abuse, uncomplicated',
    category: 'Substance Use - Other',
  },
  {
    icd10: 'F19.20',
    description: 'Other psychoactive substance dependence, uncomplicated',
    category: 'Substance Use - Other',
  },
  {
    icd10: 'F16.20',
    description: 'Hallucinogen dependence, uncomplicated',
    category: 'Substance Use - Other',
  },
  {
    icd10: 'F16.10',
    description: 'Hallucinogen abuse, uncomplicated',
    category: 'Substance Use - Other',
  },

  // -------------
  // Dissociative
  // -------------
  {
    icd10: 'F44.81',
    description: 'Dissociative identity disorder',
    category: 'Dissociative',
    synonyms: ['DID'],
  },
  {
    icd10: 'F44.9',
    description: 'Dissociative and conversion disorder, unspecified',
    category: 'Dissociative',
  },
  {
    icd10: 'F48.1',
    description: 'Depersonalization-derealization disorder',
    category: 'Dissociative',
    synonyms: ['DPDR'],
  },

  // -------
  // Somatic
  // -------
  { icd10: 'F45.1', description: 'Somatization disorder', category: 'Somatic' },
  {
    icd10: 'F45.21',
    description: 'Illness anxiety disorder',
    category: 'Somatic',
    synonyms: ['health anxiety'],
  },

  // --------
  // Cognitive
  // --------
  {
    icd10: 'R41.840',
    description: 'Attention and concentration deficit',
    category: 'Cognitive',
    isModifierLike: true,
    synonyms: ['concentration'],
  },
  {
    icd10: 'R41.89',
    description: 'Other symptoms and signs involving cognitive functions and awareness',
    category: 'Cognitive',
    isModifierLike: true,
  },

  // ----------------------------
  // Behavioral & Other Mental
  // ----------------------------
  {
    icd10: 'F64.0',
    description: 'Transsexualism',
    category: 'Behavioral & Other Mental',
    synonyms: ['gender dysphoria (icd10-era)'],
  },
  {
    icd10: 'F59',
    description:
      'Unspecified behavioral syndromes associated with physiological disturbances and physical factors',
    category: 'Behavioral & Other Mental',
  },

  // ----------------------------
  // Z-Codes: Medication/Adherence
  // ----------------------------
  {
    icd10: 'Z79.899',
    description: 'Other long term (current) drug therapy',
    category: 'Z-Codes (Medication/Adherence)',
    isModifierLike: true,
    synonyms: ['long-term medication'],
  },
  {
    icd10: 'Z91.14',
    description: "Patient's other noncompliance with medication regimen",
    category: 'Z-Codes (Medication/Adherence)',
    isModifierLike: true,
    synonyms: ['nonadherence', 'noncompliance'],
  },
  {
    icd10: 'Z91.148',
    description: "Patient's other noncompliance with other medical treatment and regimen",
    category: 'Z-Codes (Medication/Adherence)',
    isModifierLike: true,
    synonyms: ['nonadherence'],
  },
  {
    icd10: 'Z81.8',
    description: 'Family history of other mental and behavioral disorders',
    category: 'Z-Codes (Medication/Adherence)',
    isModifierLike: true,
  },
  {
    icd10: 'Z86.59',
    description: 'Personal history of other mental and behavioral disorders',
    category: 'Z-Codes (Medication/Adherence)',
    isModifierLike: true,
  },

  // --------------------------------
  // Z-Codes: Psychosocial / Context
  // --------------------------------
  {
    icd10: 'Z60.0',
    description: 'Problems of adjustment to life-cycle transitions',
    category: 'Z-Codes (Psychosocial/Context)',
    isModifierLike: true,
  },
  {
    icd10: 'Z60.2',
    description: 'Problems related to living alone',
    category: 'Z-Codes (Psychosocial/Context)',
    isModifierLike: true,
  },
  {
    icd10: 'Z60.4',
    description: 'Social exclusion and rejection',
    category: 'Z-Codes (Psychosocial/Context)',
    isModifierLike: true,
  },
  {
    icd10: 'Z62.820',
    description: 'Parent-child relational problem',
    category: 'Z-Codes (Family/Relationship)',
    isModifierLike: true,
  },
  {
    icd10: 'Z63.0',
    description: 'Problems in relationship with spouse or partner',
    category: 'Z-Codes (Family/Relationship)',
    isModifierLike: true,
    synonyms: ['relationship conflict'],
  },
  {
    icd10: 'Z63.4',
    description: 'Disappearance and death of family member',
    category: 'Z-Codes (Family/Relationship)',
    isModifierLike: true,
    synonyms: ['bereavement', 'grief'],
  },
  {
    icd10: 'Z63.5',
    description: 'Disruption of family by separation and divorce',
    category: 'Z-Codes (Family/Relationship)',
    isModifierLike: true,
  },

  // --------------------
  // Z-Codes: Work/School
  // --------------------
  {
    icd10: 'Z56.0',
    description: 'Unemployment, unspecified',
    category: 'Z-Codes (Work/School)',
    isModifierLike: true,
  },
  {
    icd10: 'Z56.3',
    description: 'Stressful work schedule',
    category: 'Z-Codes (Work/School)',
    isModifierLike: true,
  },
  {
    icd10: 'Z56.6',
    description: 'Other physical and mental strain related to work',
    category: 'Z-Codes (Work/School)',
    isModifierLike: true,
  },
  {
    icd10: 'Z55.9',
    description: 'Problems related to education and literacy, unspecified',
    category: 'Z-Codes (Work/School)',
    isModifierLike: true,
  },

  // -------------------------
  // Z-Codes: Housing / Econ
  // -------------------------
  {
    icd10: 'Z59.0',
    description: 'Homelessness',
    category: 'Z-Codes (Housing/Econ)',
    isModifierLike: true,
  },
  {
    icd10: 'Z59.1',
    description: 'Inadequate housing',
    category: 'Z-Codes (Housing/Econ)',
    isModifierLike: true,
  },
  {
    icd10: 'Z59.6',
    description: 'Low income',
    category: 'Z-Codes (Housing/Econ)',
    isModifierLike: true,
  },
  {
    icd10: 'Z59.7',
    description: 'Insufficient social insurance and welfare support',
    category: 'Z-Codes (Housing/Econ)',
    isModifierLike: true,
  },

  // ------------------------
  // Z-Codes: Legal/Forensic
  // ------------------------
  {
    icd10: 'Z65.3',
    description: 'Problems related to other legal circumstances',
    category: 'Z-Codes (Legal/Forensic)',
    isModifierLike: true,
  },
  {
    icd10: 'Z65.8',
    description: 'Other specified problems related to psychosocial circumstances',
    category: 'Z-Codes (Legal/Forensic)',
    isModifierLike: true,
  },

  // ------------------------
  // Z-Codes: Other
  // ------------------------
  {
    icd10: 'Z91.89',
    description: 'Other specified personal risk factors, not elsewhere classified',
    category: 'Z-Codes (Other)',
    isModifierLike: true,
  },
  {
    icd10: 'Z71.89',
    description: 'Other specified counseling',
    category: 'Z-Codes (Other)',
    isModifierLike: true,
  },
];

export interface ICD10CodePickerProps {
  /**
   * Selected ICD-10 codes (first is primary, up to 4 total)
   * Can also accept a single string for backwards compatibility
   */
  value?: string[] | string;
  /**
   * Callback when codes change
   */
  onChange: (codes: string[]) => void;
  /**
   * Optional callback when Enter key is pressed
   */
  onEnter?: () => void;
  /**
   * Whether the input is disabled
   */
  disabled?: boolean;
  /**
   * Whether to show the header with description and info tooltip
   * @default true
   */
  showHeader?: boolean;
  /**
   * Size of the TextField input
   * @default 'small'
   */
  size?: 'small' | 'medium';
  /**
   * Maximum number of codes allowed
   * @default 4
   */
  maxCodes?: number;
  /**
   * Provenance for each diagnosis code, keyed by normalized ICD-10 code.
   * When provided, a small source indicator (AI / Provider) is shown on each chip.
   */
  diagnosisCodeSources?: Record<string, 'provider_selected' | 'ai_recommended'>;
  /**
   * When true, render a compact add button until the user opens the autocomplete.
   */
  hideInputUntilActive?: boolean;
  /**
   * When true, place the compact add action beside selected chips when space allows.
   */
  inlineAddAction?: boolean;
}

const ICD10_INFO_TOOLTIP =
  'Select ICD-10 diagnosis codes for this encounter. The first code is the primary diagnosis. You can add up to 3 additional codes.';

const MAX_CODES_DEFAULT = 4;

/**
 * ICD-10 Code Picker component with autocomplete
 *
 * Features:
 * - Autocomplete with common psychiatric ICD-10 codes
 * - Supports multiple codes (first is primary, up to 3 additional)
 * - Grouped by category for easy browsing
 * - Supports synonyms for better search
 * - freeSolo mode allows custom codes
 *
 * @example
 * ```tsx
 * <ICD10CodePicker
 *   value={icd10Codes}
 *   onChange={(codes) => setIcd10Codes(codes)}
 *   onEnter={() => handleSave()}
 * />
 * ```
 */
export const ICD10CodePicker: React.FC<ICD10CodePickerProps> = ({
  value = [],
  onChange,
  onEnter,
  disabled = false,
  showHeader = true,
  size = 'small',
  maxCodes = MAX_CODES_DEFAULT,
  diagnosisCodeSources,
  hideInputUntilActive = false,
  inlineAddAction = false,
}) => {
  const theme = useTheme();

  // Normalize value to array
  const codes = useMemo(() => {
    if (typeof value === 'string') {
      return value ? [value] : [];
    }
    return value.filter(c => c && c.trim() !== '');
  }, [value]);

  // State for the autocomplete input
  const [inputValue, setInputValue] = React.useState('');
  const [isInputActive, setInputActive] = React.useState(!hideInputUntilActive);

  // Check if we can add more codes
  const canAddMore = codes.length < maxCodes;

  // Add a code
  const handleAddCode = useCallback(
    (code: string) => {
      if (!code || codes.includes(code) || !canAddMore) return;
      onChange([...codes, code]);
      setInputValue('');
      if (hideInputUntilActive) {
        setInputActive(false);
      }
    },
    [codes, onChange, canAddMore, hideInputUntilActive],
  );

  // Remove a code
  const handleRemoveCode = useCallback(
    (codeToRemove: string) => {
      onChange(codes.filter(c => c !== codeToRemove));
    },
    [codes, onChange],
  );

  // Make a code primary (move to first position)
  const handleMakePrimary = useCallback(
    (code: string) => {
      const filtered = codes.filter(c => c !== code);
      onChange([code, ...filtered]);
    },
    [codes, onChange],
  );

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: ICD10CodeOption | string | null) => {
      let icd10Code = '';
      if (typeof newValue === 'string') {
        // freeSolo: user typed a custom value
        icd10Code = newValue
          .toUpperCase()
          .replace(/[^A-Z0-9.-]/g, '')
          .slice(0, 10);
      } else if (newValue) {
        icd10Code = newValue.icd10;
      }
      if (icd10Code) {
        handleAddCode(icd10Code);
      }
    },
    [handleAddCode],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        const target = e.target as HTMLInputElement;
        const isDropdownOpen = target.getAttribute('aria-expanded') === 'true';
        if (!isDropdownOpen) {
          // If there's input text, add it as a code
          if (inputValue.trim()) {
            e.preventDefault();
            const cleaned = inputValue
              .toUpperCase()
              .replace(/[^A-Z0-9.-]/g, '')
              .slice(0, 10);
            if (cleaned) {
              handleAddCode(cleaned);
            }
          } else if (onEnter) {
            e.preventDefault();
            onEnter();
          }
        }
      }
    },
    [inputValue, handleAddCode, onEnter],
  );

  // Custom option rendering
  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: ICD10CodeOption) => (
    <Box component="li" {...props} key={option.icd10}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 600,
            minWidth: 80,
            color: theme.palette.text.primary,
          }}
        >
          {option.icd10}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
            {option.description}
          </Typography>
          {option.category !== 'Common' && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {option.category}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );

  // Filter options based on search (including synonyms) and exclude already selected
  const filterOptions = useCallback(
    (options: ICD10CodeOption[], { inputValue: searchInput }: { inputValue: string }) => {
      const searchLower = searchInput.toLowerCase().trim();

      // First filter out already selected codes
      let filtered = options.filter(option => !codes.includes(option.icd10));

      // Then filter by search if provided
      if (searchLower) {
        filtered = filtered.filter(option => {
          // Match ICD-10 code
          if (option.icd10.toLowerCase().includes(searchLower)) return true;
          // Match description
          if (option.description.toLowerCase().includes(searchLower)) return true;
          // Match synonyms
          if (option.synonyms?.some(syn => syn.toLowerCase().includes(searchLower))) return true;
          return false;
        });
      }

      return filtered;
    },
    [codes],
  );

  // Group options by category
  const groupBy = useCallback((option: ICD10CodeOption) => option.category, []);

  // Get description for a code
  const getCodeDescription = useCallback((code: string) => {
    const found = ICD10_CODES.find(opt => opt.icd10 === code);
    return found?.description || '';
  }, []);

  return (
    <Stack spacing={1.5}>
      {showHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            ICD-10 Codes
          </Typography>
          <Tooltip title={ICD10_INFO_TOOLTIP} arrow>
            <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            ({codes.length}/{maxCodes})
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {/* Selected codes as chips */}
        {codes.map((code, index) => {
          const isPrimary = index === 0;
          const description = getCodeDescription(code);
          return (
            <Tooltip
              key={code}
              title={
                <Box>
                  {description && <Typography variant="body2">{description}</Typography>}
                  {!isPrimary && (
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Click to make primary
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {code}
                    </Typography>
                    {diagnosisCodeSources?.[code] === 'ai_recommended' && (
                      <AutoAwesome
                        sx={{
                          color: isPrimary ? 'primary.contrastText' : 'secondary.main',
                          fontSize: 14,
                          opacity: isPrimary ? 0.9 : 1,
                        }}
                      />
                    )}
                  </Box>
                }
                size="small"
                variant={isPrimary ? 'filled' : 'outlined'}
                color={isPrimary ? 'primary' : 'default'}
                onDelete={disabled ? undefined : () => handleRemoveCode(code)}
                onClick={disabled || isPrimary ? undefined : () => handleMakePrimary(code)}
                sx={{
                  cursor: disabled || isPrimary ? 'default' : 'pointer',
                  '&:hover':
                    !disabled && !isPrimary
                      ? {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }
                      : undefined,
                }}
              />
            </Tooltip>
          );
        })}

        {/* Autocomplete for adding codes */}
        {canAddMore && hideInputUntilActive && !isInputActive && inlineAddAction && (
          <Button
            size="small"
            variant="text"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setInputActive(true)}
            disabled={disabled}
            sx={{ textTransform: 'none' }}
          >
            Add diagnosis
          </Button>
        )}
      </Box>

      {canAddMore && hideInputUntilActive && !isInputActive && !inlineAddAction && (
        <Button
          size="small"
          variant="text"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setInputActive(true)}
          disabled={disabled}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          Add diagnosis
        </Button>
      )}

      {canAddMore && (!hideInputUntilActive || isInputActive) && (
        <Autocomplete<ICD10CodeOption, false, true, true>
          freeSolo
          value={undefined}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue, reason) => {
            if (reason === 'input') {
              const cleaned = newInputValue
                .toUpperCase()
                .replace(/[^A-Z0-9.-]/g, '')
                .slice(0, 10);
              setInputValue(cleaned);
            } else if (reason === 'clear') {
              setInputValue('');
            }
          }}
          onChange={handleChange}
          options={ICD10_CODES}
          getOptionLabel={option => {
            if (typeof option === 'string') return option;
            return option.icd10;
          }}
          filterOptions={filterOptions}
          groupBy={groupBy}
          renderOption={renderOption}
          renderInput={params => (
            <TextField
              {...params}
              placeholder={
                codes.length === 0
                  ? 'Search for primary ICD-10 code...'
                  : 'Add another ICD-10 code...'
              }
              size={size}
              disabled={disabled}
              onKeyDown={handleKeyDown}
              autoFocus={hideInputUntilActive}
              onBlur={() => {
                if (hideInputUntilActive && !inputValue.trim()) {
                  setInputActive(false);
                }
              }}
              slotProps={{
                ...params.slotProps,
                inputLabel: { ...params.slotProps.inputLabel, shrink: true },
                htmlInput: {
                  ...params.slotProps.htmlInput,
                  maxLength: 10,
                },
              }}
            />
          )}
          slotProps={{ listbox: { sx: { maxHeight: 400 } } }}
          sx={{
            '& .MuiAutocomplete-input': {
              fontFamily: 'inherit',
            },
          }}
        />
      )}

      {/* Max codes reached message */}
      {!canAddMore && (
        <Typography variant="caption" color="text.secondary">
          Maximum {maxCodes} codes reached
        </Typography>
      )}
    </Stack>
  );
};
